import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../contexts/AuthContext';
import '../../css/ApiKeysModal.css';

const DOCS_URL = 'https://bluebird-documentation.com';

/**
 * Personal API key management for Bluebird Task Management.
 *
 * These are the SAME personal ("bb_") keys used across the Bluebird Suite —
 * a key generated here (or in Bluebird Documentation) works against every
 * product's agent API, including the task endpoints documented below. Workspace
 * ("bbw_") keys are workspace-specific and managed in Bluebird Documentation's
 * Workspace Settings, so they are explained here but not generated here.
 */
const ApiKeysModal = ({ onClose }) => {
    const { apiCall } = useAuth();

    const [apiKeys, setApiKeys] = useState([]);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [generatedKey, setGeneratedKey] = useState(null);

    // New-key form
    const [newKeyName, setNewKeyName] = useState('');
    const [newKeyScopes, setNewKeyScopes] = useState({ read: true, write: false, delete: false });
    const [newKeyRPH, setNewKeyRPH] = useState(100);

    const fetchApiKeys = useCallback(async () => {
        try {
            const res = await apiCall('/users/api-keys/');
            if (res.ok) {
                const data = await res.json();
                setApiKeys(data.api_keys || []);
            }
        } catch (e) {
            console.error('Failed to load API keys:', e);
        }
    }, [apiCall]);

    useEffect(() => {
        fetchApiKeys();
    }, [fetchApiKeys]);

    const createApiKey = async () => {
        if (!newKeyName.trim()) {
            setError('Key name is required.');
            return;
        }
        const scopes = Object.entries(newKeyScopes).filter(([, v]) => v).map(([k]) => k);
        if (scopes.length === 0) {
            setError('Select at least one permission scope.');
            return;
        }
        setLoading(true);
        setError('');
        try {
            const res = await apiCall('/users/api-keys/create/', {
                method: 'POST',
                body: JSON.stringify({ name: newKeyName.trim(), scopes, requests_per_hour: newKeyRPH }),
            });
            const data = await res.json();
            if (res.ok) {
                setGeneratedKey(data.key);
                setNewKeyName('');
                setNewKeyScopes({ read: true, write: false, delete: false });
                setNewKeyRPH(100);
                fetchApiKeys();
            } else {
                setError(data.error || 'Failed to create API key.');
            }
        } catch (e) {
            setError('Failed to create API key.');
        } finally {
            setLoading(false);
        }
    };

    const revokeApiKey = async (keyId, keyName) => {
        if (!window.confirm(`Revoke the key "${keyName}"? This cannot be undone.`)) return;
        try {
            const res = await apiCall(`/users/api-keys/${keyId}/revoke/`, { method: 'DELETE' });
            if (res.ok) {
                fetchApiKeys();
            } else {
                setError('Failed to revoke key.');
            }
        } catch (e) {
            setError('Failed to revoke key.');
        }
    };

    return createPortal(
        <div className="ApiKeysModal__overlay" onClick={onClose}>
            <div className="ApiKeysModal" onClick={(e) => e.stopPropagation()}>
                <div className="ApiKeysModal__header">
                    <div className="ApiKeysModal__header-left">
                        <i className="fas fa-key" />
                        <h2 className="ApiKeysModal__title">API Keys</h2>
                    </div>
                    <button className="ApiKeysModal__close" onClick={onClose} aria-label="Close">×</button>
                </div>

                <div className="ApiKeysModal__body">
                    <p className="ApiKeysModal__intro">
                        Generate a key to let external tools and AI agents read and manage your tasks
                        programmatically. <strong>These are the same keys used across the Bluebird Suite</strong> —
                        a key you already created in Bluebird Documentation works here too. Each key is shown
                        only once, so save it somewhere safe.
                    </p>

                    {/* ─── How to use ─────────────────────────────────────────── */}
                    <div className="ApiKeysModal__info">
                        <p className="ApiKeysModal__info-title">
                            <i className="fas fa-circle-info" /> Using your key with an AI agent
                        </p>
                        <p>Store the key in a <code>.env</code> file in your project:</p>
                        <code className="ApiKeysModal__code-block">BLUEBIRD_API_KEY=bb_your_key_here</code>
                        <p>Then pass it as the <code>Authorization</code> header (no <code>Bearer</code> prefix):</p>
                        <code className="ApiKeysModal__code-block">Authorization: bb_your_key_here</code>
                        <p>
                            Task endpoints (base URL{' '}
                            <code>bluebirddocumentationadmin.pythonanywhere.com</code>):
                        </p>
                        <ul className="ApiKeysModal__endpoints">
                            <li><code>GET /tasks/agent/boards/</code> — list your boards</li>
                            <li><code>GET /tasks/agent/boards/&#123;board_id&#125;/</code> — board with columns &amp; tasks</li>
                            <li><code>GET /tasks/agent/boards/&#123;board_id&#125;/tasks/</code> — list tasks (filter by label, priority…)</li>
                            <li><code>POST /tasks/agent/boards/&#123;board_id&#125;/tasks/</code> — create a task</li>
                            <li><code>GET · PUT · DELETE /tasks/agent/boards/&#123;board_id&#125;/tasks/&#123;number&#125;/</code> — read, edit, delete a task</li>
                            <li><code>GET /tasks/agent/boards/&#123;board_id&#125;/archive/</code> — view the archive library</li>
                            <li><code>POST /tasks/agent/boards/&#123;board_id&#125;/tasks/&#123;number&#125;/archive/</code> — archive a task</li>
                            <li><code>POST /tasks/agent/boards/&#123;board_id&#125;/tasks/&#123;number&#125;/unarchive/</code> — unarchive a task</li>
                        </ul>
                        <p className="ApiKeysModal__info-note">
                            Address tasks by their board number (the <code>42</code> in <code>BBTM-42</code>).
                            Archived tasks are read-only — unarchive one before editing it. The full machine-readable
                            spec is public at <code>/tasks/agent/spec/</code>.
                        </p>
                    </div>

                    {/* ─── Workspace keys note ────────────────────────────────── */}
                    <div className="ApiKeysModal__info ApiKeysModal__info--workspace">
                        <p className="ApiKeysModal__info-title">
                            <i className="fas fa-users" /> Team Workspace keys
                        </p>
                        <p>
                            Workspace keys (<code>bbw_</code>) are scoped to a <strong>single</strong> workspace and
                            reach boards linked to it. They are generated by a workspace member in{' '}
                            <a href={DOCS_URL} target="_blank" rel="noopener noreferrer">Bluebird Documentation</a> →
                            Workspace Settings → API Keys, and used against{' '}
                            <code>/tasks/agent/...</code> the same way as a personal key.
                        </p>
                    </div>

                    {/* ─── Freshly generated key ──────────────────────────────── */}
                    {generatedKey && (
                        <div className="ApiKeysModal__new-key">
                            <p className="ApiKeysModal__new-key-warn">
                                <i className="fas fa-triangle-exclamation" /> Save this key now — it will not be shown again.
                            </p>
                            <div className="ApiKeysModal__new-key-row">
                                <code className="ApiKeysModal__new-key-value">{generatedKey}</code>
                                <button
                                    className="ApiKeysModal__btn"
                                    onClick={() => { navigator.clipboard.writeText(generatedKey); }}
                                >
                                    <i className="fas fa-copy" /> Copy
                                </button>
                            </div>
                            <button
                                className="ApiKeysModal__btn ApiKeysModal__btn--ghost"
                                onClick={() => setGeneratedKey(null)}
                            >
                                I've saved my key
                            </button>
                        </div>
                    )}

                    {error && <div className="ApiKeysModal__error">{error}</div>}

                    {/* ─── Existing keys ──────────────────────────────────────── */}
                    {apiKeys.length > 0 && (
                        <div className="ApiKeysModal__list">
                            {apiKeys.map(key => (
                                <div key={key.id} className="ApiKeysModal__key">
                                    <div className="ApiKeysModal__key-top">
                                        <div>
                                            <span className="ApiKeysModal__key-name">{key.name}</span>
                                            <span className="ApiKeysModal__key-prefix">{key.key_prefix}…</span>
                                            {!key.is_active && <span className="ApiKeysModal__key-revoked">(Revoked)</span>}
                                        </div>
                                        {key.is_active && (
                                            <button
                                                className="ApiKeysModal__btn ApiKeysModal__btn--danger"
                                                onClick={() => revokeApiKey(key.id, key.name)}
                                            >
                                                Revoke
                                            </button>
                                        )}
                                    </div>
                                    <div className="ApiKeysModal__key-meta">
                                        <span>Scopes: <strong>{(key.scopes || []).join(', ')}</strong></span>
                                        <span>{key.requests_per_hour} req/hr</span>
                                        {key.last_used_at && <span>Last used: {new Date(key.last_used_at).toLocaleDateString()}</span>}
                                        {key.expires_at && <span>Expires: {new Date(key.expires_at).toLocaleDateString()}</span>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* ─── Create new key ─────────────────────────────────────── */}
                    <div className="ApiKeysModal__create">
                        <h3 className="ApiKeysModal__create-title">Create New API Key</h3>

                        <div className="ApiKeysModal__field">
                            <label className="ApiKeysModal__label">Key Name</label>
                            <input
                                type="text"
                                placeholder="e.g. Claude Code, Automation Script"
                                value={newKeyName}
                                onChange={(e) => setNewKeyName(e.target.value)}
                                className="ApiKeysModal__input"
                            />
                        </div>

                        <div className="ApiKeysModal__field">
                            <label className="ApiKeysModal__label">Permissions</label>
                            <div className="ApiKeysModal__scopes">
                                {['read', 'write', 'delete'].map(scope => (
                                    <label key={scope} className="ApiKeysModal__scope">
                                        <input
                                            type="checkbox"
                                            checked={newKeyScopes[scope]}
                                            onChange={(e) => setNewKeyScopes(prev => ({ ...prev, [scope]: e.target.checked }))}
                                        />
                                        <span>{scope}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        <div className="ApiKeysModal__field">
                            <label className="ApiKeysModal__label">Rate Limit</label>
                            <select
                                className="ApiKeysModal__select"
                                value={newKeyRPH}
                                onChange={(e) => setNewKeyRPH(parseInt(e.target.value))}
                            >
                                <option value={50}>50 requests / hour</option>
                                <option value={100}>100 requests / hour</option>
                                <option value={200}>200 requests / hour</option>
                                <option value={500}>500 requests / hour</option>
                            </select>
                        </div>

                        <button
                            className="ApiKeysModal__btn ApiKeysModal__btn--primary"
                            onClick={createApiKey}
                            disabled={loading}
                        >
                            {loading ? 'Generating…' : 'Generate Key'}
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default ApiKeysModal;
